/**
 * Auto-Scroll Extension for ScrollCarousel
 * Adds smooth continuous infinite scroll functionality to scroll-carousel elements
 * Inspired by the marquee-text component
 */

class AutoScrollCarousel {
  constructor(carousel) {
    this.carousel = carousel;
    this.animationFrameId = null;
    this.isPaused = false;
    this.scrollPosition = 0;
    this.isDuplicated = false;

    // Get settings from attributes
    this.enabled = carousel.hasAttribute('auto-scroll');
    this.speed = parseFloat(carousel.getAttribute('auto-scroll-speed') || '0.3');
    this.pauseOnHover = carousel.hasAttribute('pause-on-hover');

    if (this.enabled) {
      this.init();
    }
  }

  init() {
    console.log('AutoScrollCarousel init', this.carousel);

    // Wait for carousel to be fully loaded
    setTimeout(() => {
      console.log('Starting duplication and scroll');
      this.duplicateContent();
      this.setupEventListeners();
      this.start();
    }, 100);
  }

  /**
   * Duplicate carousel content for seamless infinite loop
   * Similar to marquee-text implementation
   */
  duplicateContent() {
    const cells = Array.from(this.carousel.children);

    console.log('Cells found:', cells.length);

    if (cells.length === 0) {
      return;
    }

    // Clone all cells to create seamless loop
    cells.forEach(cell => {
      const clone = cell.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      this.carousel.appendChild(clone);
    });

    this.isDuplicated = true;
    this.totalWidth = this.carousel.scrollWidth / 2; // Half because we duplicated

    console.log('Content duplicated. Total width:', this.totalWidth);
  }

  setupEventListeners() {
    // Pause on hover if enabled
    if (this.pauseOnHover) {
      this.carousel.addEventListener('mouseenter', () => this.pause());
      this.carousel.addEventListener('mouseleave', () => this.resume());
    }

    // Pause on user interaction
    this.carousel.addEventListener('mousedown', () => this.pause());
    this.carousel.addEventListener('touchstart', () => this.pause());

    // CRITICAL: Force disable scroll-snap on both carousel and children
    this.carousel.style.setProperty('scroll-behavior', 'auto', 'important');
    this.carousel.style.setProperty('scroll-snap-type', 'none', 'important');

    // Disable scroll-snap on all child elements
    Array.from(this.carousel.children).forEach(child => {
      child.style.setProperty('scroll-snap-align', 'none', 'important');
      child.style.setProperty('scroll-snap-stop', 'normal', 'important');
    });

    // Force a reflow to ensure proper width calculation
    void this.carousel.offsetWidth;

    console.log('Event listeners set up, scroll-snap disabled');
    console.log('Carousel scrollWidth:', this.carousel.scrollWidth, 'clientWidth:', this.carousel.clientWidth);
  }

  start() {
    this.isPaused = false;
    this.animate();
  }

  stop() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  pause() {
    this.isPaused = true;
  }

  resume() {
    this.isPaused = false;
    if (!this.animationFrameId) {
      this.animate();
    }
  }

  /**
   * Smooth continuous scroll animation
   * Uses requestAnimationFrame for smooth 60fps scrolling
   */
  animate() {
    if (this.isPaused) {
      this.animationFrameId = null;
      return;
    }

    // Calculate scroll amount based on speed
    // Speed ranges from 0.1 (slowest) to 1.0 (fastest)
    // Adjust the multiplier to control overall scrolling pace
    const scrollAmount = this.speed * 2; // Pixels per frame (at 60fps)

    // Update scroll position
    this.scrollPosition += scrollAmount;

    // Reset when we've scrolled through the original content
    if (this.scrollPosition >= this.totalWidth) {
      console.log('Resetting scroll position');
      this.scrollPosition = 0;
    }

    // Apply scroll
    this.carousel.scrollLeft = this.scrollPosition;

    // Log occasionally for debugging
    if (Math.floor(this.scrollPosition) % 100 === 0) {
      console.log('Scroll position:', this.scrollPosition, 'scrollLeft:', this.carousel.scrollLeft);
    }

    // Continue animation
    this.animationFrameId = requestAnimationFrame(() => this.animate());
  }
}

// Initialize auto-scroll for all scroll-carousel elements with auto-scroll attribute
function initAutoScroll() {
  console.log('initAutoScroll called');
  const carousels = document.querySelectorAll('scroll-carousel[auto-scroll]');
  console.log('Found carousels with auto-scroll:', carousels.length);

  carousels.forEach(carousel => {
    console.log('Carousel element:', carousel, 'has auto-scroll:', carousel.hasAttribute('auto-scroll'));
    // Wait for the custom element to be fully defined and connected
    if (customElements.get('scroll-carousel')) {
      customElements.whenDefined('scroll-carousel').then(() => {
        console.log('scroll-carousel defined, initializing AutoScrollCarousel');
        // Small delay to ensure the carousel is fully initialized
        setTimeout(() => {
          new AutoScrollCarousel(carousel);
        }, 200);
      });
    }
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAutoScroll);
} else {
  initAutoScroll();
}

// Re-initialize for dynamically added carousels (e.g., in Shopify theme editor)
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', initAutoScroll);
}
